import { FlakinessReport } from '@flakiness/flakiness-report';
import { Multimap } from '@flakiness/shared/common/multimap.js';
import { LitElement, css, html, nothing } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';
import { linkStyles } from './cssstyles.js';

// directly import the theme and language modules, only the ones you imported will be bundled.
import githubLight from '@shikijs/themes/github-light';

import bash from '@shikijs/langs/bash';
import java from '@shikijs/langs/java';
import javascript from '@shikijs/langs/javascript';
import powershell from '@shikijs/langs/powershell';
import python from '@shikijs/langs/python';
import toml from '@shikijs/langs/toml';
import typescript from '@shikijs/langs/typescript';
import yaml from '@shikijs/langs/yaml';
import { createHighlighterCore } from 'shiki/core';
import { createOnigurumaEngine } from 'shiki/engine/oniguruma';
import shikiWasm from 'shiki/wasm';

const highlighter = await createHighlighterCore({
  themes: [
    githubLight,
  ],
  langs: [
    bash,
    yaml,
    typescript,
    python,
    java,
    javascript,
    powershell,
    toml,
  ],
  engine: createOnigurumaEngine(shikiWasm),
});

export type HighlightOptions = {
  /** Inclusive 1-based line ranges to render as inserted (green diff highlight). */
  ins?: Array<[number, number]>;
};

export function htmlHighlight(text: string, lang: string, options?: HighlightOptions) {
  const transformers: Parameters<typeof highlighter.codeToHtml>[1]['transformers'] = [];
  const insRanges = options?.ins;
  if (insRanges?.length) {
    transformers.push({
      line(node, lineNum) {
        // Shiki's `line` parameter is 1-based.
        if (insRanges.some(([start, end]) => lineNum >= start && lineNum <= end))
          this.addClassToHast(node, 'ins-line');
      }
    });
  }
  return highlighter.codeToHtml(text, {
    lang,
    theme: 'github-light',
    rootStyle: 'margin: 0; padding: 0;',
    transformers,
  });
}

type Source = {
  contentType?: string,
  lines: string[],
  lineOffset: FlakinessReport.Number1Based,
}

type Snippet = {
  lang: string,
  lines: string[],
  lineOffset: FlakinessReport.Number1Based,
  column: FlakinessReport.Number1Based,
}

class ReportSourceData {
  private _sources = new Multimap<FlakinessReport.GitFilePath, Source>();

  constructor(report: FlakinessReport.Report) {
    for (const source of report.sources ?? []) {
      this._sources.set(source.filePath, {
        lineOffset: (source.lineOffset ?? 1) as FlakinessReport.Number1Based,
        contentType: source.contentType,
        lines: source.text.split('\n'),
      });
    }
  }

  snippet(location: FlakinessReport.Location, context: number): Snippet|undefined {
    const sources = this._sources.getAll(location.file);
    if (!sources.length)
      return undefined;
    for (const source of sources) {
      let lineOffset = (location.line - context) as FlakinessReport.Number1Based;
      if (lineOffset < source.lineOffset)
        lineOffset = source.lineOffset;
      const lines = source.lines.slice(
        lineOffset - source.lineOffset,
        location.line + context + 1 - source.lineOffset,
      );
      if (!lines.length)
        continue;
      const truncated = truncateSnippetLines(lines, location, lineOffset);

      return {
        lines: truncated.lines,
        lineOffset,
        column: truncated.column,
        lang: detectLanguage(source.contentType, location.file) ?? '',
      };
    }
    return undefined;
  }
}

const parsedSources = new WeakMap<FlakinessReport.Report, ReportSourceData>();

export function createCodeSnippet(report?: FlakinessReport.Report, location?: FlakinessReport.Location, context?: number) {
  if (!report || !location)
    return undefined;
  let data = parsedSources.get(report);
  if (!data) {
    data = new ReportSourceData(report);
    parsedSources.set(report, data);
  }
  return data.snippet(location, context ?? 1);
}

const MAX_SNIPPET_LINE_CHARS = 200;
const TRUNCATION_MARKER = '…';
const GUTTER_WIDTH = 5;

@customElement('code-snippet')
class CodeSnippet extends LitElement {
  @property({ attribute: false, }) snippet?: Snippet;
  @property({ attribute: false, }) location?: FlakinessReport.Location;

  render() {
    const snippet = this.snippet;
    if (!snippet)
      return nothing;
    const lineOffset = snippet.lineOffset;
    const location = this.location;

    const code = highlighter.codeToHtml(snippet.lines.join('\n'), {
      lang: snippet.lang,
      theme: 'github-light',
      rootStyle: 'background-color: transparent; margin: 0; padding: 0;',
      transformers: [{
        line(node, lineIndex) {
          const currentLineNum = lineOffset + lineIndex - 1;
          node.children = [{
            type: 'element',
            tagName: 'span',
            properties: { className: ['gutter'] },
            children: [{ type: 'text', value: `${currentLineNum.toString().padStart(GUTTER_WIDTH)} | ` }]
          }, ...node.children];
          if (currentLineNum === location?.line)
            this.addClassToHast(node, 'error-line');
        },
        code(node) {
          if (!location || !snippet)
            return;
          const focusLineIndex = location.line - snippet.lineOffset;
          if (focusLineIndex < 0 || focusLineIndex >= snippet.lines.length)
            return;
          // Mirror the focus line's leading whitespace verbatim (keeping tabs as
          // tabs) so the caret lands under the right column. Replacing tabs with
          // single spaces would misplace the caret for tab-indented sources,
          // since a tab renders to the next tab stop in <pre>.
          const caretPrefix = snippet.lines[focusLineIndex].slice(0, snippet.column - 1);
          const caretIndent = caretPrefix.replace(/[^\t]/g, ' ') +
            ' '.repeat(Math.max(0, snippet.column - 1 - caretPrefix.length));
          const insertionIndex = Math.min(node.children.length, (focusLineIndex + 1) * 2);
          const needsLeadingNewline = node.children[insertionIndex - 1]?.type !== 'text';
          node.children.splice(insertionIndex, 0,
            ...(needsLeadingNewline ? [{
              type: 'text' as const,
              value: `\n`,
            }] : []), {
            type: 'element',
            tagName: 'span',
            properties: { className: ['line', 'caret-line'] },
            children: [
              {
                type: 'element',
                tagName: 'span',
                properties: { className: ['gutter'] },
                children: [{ type: 'text', value: `${' '.repeat(GUTTER_WIDTH)} | ` }]
              },
              {
                type: 'element',
                tagName: 'span',
                properties: { className: ['caret'] },
                children: [{ type: 'text', value: `${caretIndent}^` }]
              }
            ],
          }, {
            type: 'text',
            value: `\n`,
          });
        }
      }],
    });
    return html`
      <pre>${unsafeHTML(code)}</pre>
    `;
  }

  static styles = [linkStyles, css`
    :host {
      display: inline-block;
      font-size: 12px;
    }

    .gutter {
      color: #666666;
    }

    pre {
      margin: 0px;
      overflow: auto;
      background-color: var(--sl-color-neutral-50);
      padding: var(--sl-spacing-medium);
      border-radius: var(--sl-border-radius-medium);
    }
  `];
}

function truncateSnippetLines(
  lines: string[],
  location: FlakinessReport.Location,
  lineOffset: FlakinessReport.Number1Based,
): { lines: string[], column: FlakinessReport.Number1Based } {
  let column = location.column;
  const truncatedLines = lines.map((line, index) => {
    if (line.length <= MAX_SNIPPET_LINE_CHARS)
      return line;
    const currentLine = lineOffset + index;
    const isFocusLine = currentLine === location.line;
    const PREFIX_CONTEXT_SIZE = 20;
    let start = isFocusLine ? Math.max(location.column - PREFIX_CONTEXT_SIZE, 0) : 0;
    let end = start + MAX_SNIPPET_LINE_CHARS;
    if (end > line.length) {
      end = line.length;
      start = Math.max(0, end - MAX_SNIPPET_LINE_CHARS);
    }
    const prefix = start > 0 ? TRUNCATION_MARKER : '';
    const suffix = end < line.length ? TRUNCATION_MARKER : '';
    if (isFocusLine)
      column = (location.column - start + prefix.length) as FlakinessReport.Number1Based;
    return `${prefix}${line.slice(start, end)}${suffix}`;
  });
  return { lines: truncatedLines, column };
}

type SupportedLanguage = 'java' | 'python' | 'typescript' | 'javascript';

const mimeMap: Record<string, SupportedLanguage> = {
  'text/x-java-source': 'java',
  'text/x-python': 'python',
  'application/x-python-code': 'python',
  'application/javascript': 'javascript',
  'text/javascript': 'javascript',
};

const extensionMap: Record<string, SupportedLanguage> = {
  'java': 'java',
  'py': 'python',
  'ts': 'typescript',
  'tsx': 'typescript',
  'js': 'javascript',
  'jsx': 'javascript',
  'mjs': 'javascript',
}

function detectLanguage(contentType: string|undefined, file: string): SupportedLanguage|undefined {
  if (contentType && mimeMap[contentType])
    return mimeMap[contentType];
  const extension = file.split('.').pop()?.toLowerCase();
  return extension ? extensionMap[extension] : undefined;
}
